import {assert} from 'Asserts';
import {
  Grid,
  AccessibleGridCell,
  GridSummary,
  GridCell,
  isAccessibleGridCell,
  FullyAccessibleGrid,
} from 'constants/SampleConstants';
import {FillDirection} from 'lookup-config/LookupConfigModalConstants';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import APIUtil from 'util/APIUtil';

/**
 * Given a grid, get all the API IDs of all the contained items in all the containers in the grid.
 */
export function getContainerContentIdsFromGrid(grid: FullyAccessibleGrid): Array<string> {
  let ids: Array<string> = [];
  grid.cells.forEach((row) => {
    row.forEach((cell) => {
      if (cell != null) {
        ids = ids.concat(cell.container.contents.map((c) => c.containedItemId));
      }
    });
  });
  return [...new Set(ids)];
}

/**
 * Flatten a 2-dimensional array of cells and filter out any empty cells.
 * @param cells
 */
export function flattenAndFilterCells(cells: Array<Array<GridCell | null>>): Array<GridCell> {
  return cells
    .flat()
    .filter((cell): cell is GridCell => Boolean(cell))
    .filter((cell) => cell.container);
}

/**
 * Given a grid and a list of selected cells in that grid, sorts the selected cells by grid position,
 * first by row then by column.
 * @param grid
 * @param selectedCells
 */
export function getSortedSelectedCells(
  grid: Grid,
  selectedCells: Array<AccessibleGridCell>
): Array<AccessibleGridCell> {
  if (!grid) {
    return [];
  }
  const cells = selectedCells
    .map(({columnIndex, rowIndex}: AccessibleGridCell) => grid.cells[rowIndex][columnIndex])
    .filter((cell): cell is AccessibleGridCell => isAccessibleGridCell(cell));
  cells.sort(
    (a: AccessibleGridCell, b: AccessibleGridCell) => a.rowIndex - b.rowIndex || a.columnIndex - b.columnIndex
  );
  return cells;
}

/**
 * Given a grid object, returns the number of available (empty) positions.
 * @param grid
 * @returns
 */
export function getNumAvailableGridCells(grid: Grid): number {
  return grid.cells.flatMap((cell) => cell).filter((cell) => !cell).length;
}

export function getTotalGridCapacity(grid: GridSummary): number {
  return grid.storableSchema.height * grid.storableSchema.width;
}

export function getFillDirectionText(fillDirection: FillDirection): string {
  return fillDirection === FillDirection.ACROSS_ROWS ? 'Across rows' : 'Down columns';
}

export function getFillDirectionIconName(fillDirection: FillDirection): BenchlingIconName {
  return fillDirection === FillDirection.ACROSS_ROWS ? 'fill-across-rows' : 'fill-down-columns';
}

export function getGridTypeName(gridId: string): string {
  const isBox = APIUtil.isBox(gridId);
  const isPlate = APIUtil.isPlate(gridId);
  assert(isBox || isPlate, `GridUtil.getGridTypeName: gridId ${gridId} is not a box or plate`);
  return isBox ? 'box' : isPlate ? 'plate' : 'grid';
}
